import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace MSRegister {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface MSRegisterHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface MSRegisterEntity {
        componentType: string;
        policyCat: string;
        policyCriticality: string;
        policyDesc: string;
        policyHistory: MSRegisterHistoryItem[];
        policyID: string;
        policyIDstatus: "active" | "inactive" | "deleted";
        policyLocation: string;
        policyOwner: string;
        policyPublishInPrivacyMSRegister: "Yes" | "No";
        policyRisk: string;
        policySubprocessor: "Yes" | "No";
        policyTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface MSRegisterResponse {
        statusCode?: number;
        message?: string;
        policies?: MSRegisterEntity[];
        entry?: MSRegisterEntity;
        error?: string | null;
    }

    export class MSRegisterInstance {
        private data: MSRegister.MSRegisterEntity;

        constructor(data: MSRegister.MSRegisterEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): MSRegister.MSRegisterEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the MSRegister class
        public async edit(updates: Partial<MSRegister.MSRegisterEntity>): Promise<MSRegister.MSRegisterResponse | void> {
            return MSRegister.MSRegisterItem.Edit(this.data.policyID, updates);
        }
    }

    export class MSRegisterItem {

        /**
         * Add an policy to the API.
         * @param id - The ID of the policy to add.
         * @param policyData - Partial policy data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            policyData: Partial<MSRegisterEntity>,
            callback?: (res: MSRegisterResponse) => any
        ): Promise<MSRegisterResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/ismsRegister/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Delete an policy from the API.
         * @param id - The ID of the policy to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: MSRegisterResponse) => any
        ): Promise<MSRegisterResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/ismsRegister/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing policy in the API.
         * @param id - The ID of the policy to edit.
         * @param policyData - Partial policy data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            policyData: Partial<MSRegisterEntity>,
            callback?: (res: MSRegisterResponse) => any
        ): Promise<MSRegisterResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/ismsRegister/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all policies from the API.
         */
        static async Get(): Promise<MSRegister.MSRegisterEntity[] | undefined>;

        /**
         * Fetch a specific policy by ID from the API.
         * @param id - The ID of the policy to fetch.
         */
        static async Get(id: string | number): Promise<MSRegisterInstance | undefined>;

        static async Get(id?: string | number): Promise<MSRegister.MSRegisterEntity[] | MSRegisterInstance | undefined> {
            if (typeof id === 'undefined' || id === 'all') {
                // Fetch all policies
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/ismsRegister/checkRegister`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.ismsRegisterItems.sort((a, b) => ((a.id ?? 0) > (b.id ?? 0) ? 1 : ((b.id ?? 0) > (a.id ?? 0) ? -1 : 0)));
            } else {
                // Fetch single policy
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/ismsRegister/getEntry`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID, entryId: id })
                });
                
                if (res.data) {
                    return new MSRegisterInstance(res.data);
                } else {
                    return undefined;
                }
            }
        }
    }

    export const Entry = new Proxy(MSRegisterItem, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            }

            return target[lowerCasedProp];
        }
    });
}